import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, Trash2, Globe2, Smartphone, Clock, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDuration(secs) {
  if (!secs && secs !== 0) return "—";
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export function LoginHistoryModal({ open, onClose, userId, userName, token }) {
  const [history, setHistory] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/login-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data.history || []);
      setAttempts(res.data.rejected_attempts || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open && userId) fetchData(); /* eslint-disable-next-line */ }, [open, userId]);

  const handleClear = async () => {
    if (!window.confirm("Delete ALL login history & rejected attempts for this user?")) return;
    await axios.delete(`${API}/admin/users/${userId}/login-history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setHistory([]);
    setAttempts([]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-white" data-testid="login-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-pink-600">
            <History className="w-5 h-5" /> Login History · {userName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-between items-center mb-3">
          <span className="text-xs text-zinc-500">{history.length} sessions · {attempts.length} rejected attempts</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="gap-1">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
            </Button>
            <Button variant="destructive" size="sm" onClick={handleClear} className="gap-1" data-testid="clear-history-btn">
              <Trash2 className="w-3 h-3" /> Clear All
            </Button>
          </div>
        </div>

        {/* Successful logins */}
        <div className="mb-4">
          <h3 className="text-sm font-bold text-zinc-700 mb-2">Successful Sessions</h3>
          {history.length === 0 ? (
            <p className="text-xs text-zinc-400 italic">No sessions recorded.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-pink-200 text-zinc-500 text-left">
                    <th className="py-2 px-2">Login Time</th>
                    <th className="py-2 px-2">Logout Time</th>
                    <th className="py-2 px-2"><Clock className="w-3 h-3 inline" /> Duration</th>
                    <th className="py-2 px-2"><Globe2 className="w-3 h-3 inline" /> Country</th>
                    <th className="py-2 px-2">IP</th>
                    <th className="py-2 px-2"><Smartphone className="w-3 h-3 inline" /> Device</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i} className="border-b border-pink-50 last:border-0">
                      <td className="py-2 px-2 whitespace-nowrap">{fmtDate(h.login_time)}</td>
                      <td className="py-2 px-2 whitespace-nowrap">{h.logout_time ? fmtDate(h.logout_time) : <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Active</Badge>}</td>
                      <td className="py-2 px-2">{fmtDuration(h.duration_seconds)}</td>
                      <td className="py-2 px-2">{h.country || "—"}{h.city && <span className="text-zinc-400"> · {h.city}</span>}</td>
                      <td className="py-2 px-2 font-mono text-zinc-600">{h.ip || "—"}</td>
                      <td className="py-2 px-2 max-w-xs truncate text-zinc-600" title={h.user_agent}>{h.user_agent?.split(") ")[0] + ")" || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Rejected attempts */}
        {attempts.length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> Rejected Attempts ({attempts.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-red-200 text-zinc-500 text-left">
                    <th className="py-2 px-2">Time</th>
                    <th className="py-2 px-2">Reason</th>
                    <th className="py-2 px-2">IP</th>
                    <th className="py-2 px-2">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((a, i) => (
                    <tr key={i} className="border-b border-red-50 last:border-0">
                      <td className="py-2 px-2 whitespace-nowrap">{fmtDate(a.timestamp)}</td>
                      <td className="py-2 px-2"><Badge className="bg-red-100 text-red-700 border-0 text-[10px]">{a.status}</Badge></td>
                      <td className="py-2 px-2 font-mono text-zinc-600">{a.ip || "—"}</td>
                      <td className="py-2 px-2 max-w-xs truncate text-zinc-600" title={a.user_agent}>{a.user_agent?.split(") ")[0] + ")" || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
